import React, { useState } from 'react';

/**
 * "Trusted by" social-proof strip — placed directly under the Hero so a
 * skeptical visitor's next visual after the headline is recognisable
 * customer signal.
 *
 * Design: infinite horizontal marquee (the standard top-tier SaaS
 * treatment — Stripe/Vercel/Linear all do a moving logo rail). Each
 * entry renders as a real logo image when one is available, else a
 * monogram tile + wordmark so it still reads like a "logo". Edge masks
 * fade the rail into the background.
 *
 * Adding a logo: drop the file in /public (or /public/logos) and point
 * the customer's `logo` at it. Until the file exists, LogoMark falls
 * back to the monogram+name automatically (onError), so a missing file
 * never breaks the rail.
 *
 * Motion: pauses on hover; disabled under `prefers-reduced-motion`.
 */
export default function LogoCloud() {
  // Real clients. `logo` is optional — set it to a file in /public and
  // the image renders; otherwise the monogram+name shows. kinzy.png and
  // grandgaming.png already exist in /public. For the rest, drop a file
  // at the /logos/* path below and it appears with no code change.
  const customers = [
    // Real logo files in /public. If any file is renamed/removed,
    // LogoMark falls back to a name badge automatically (onError).
    { name: 'Kinzy Club', logo: '/kinzy.png' },
    { name: 'Grand Gaming', logo: '/grandgaming.png' },
    { name: 'Idyaite', logo: '/idyaite.png' },
    { name: 'Creowis', logo: '/creowis.webp' },
    { name: 'key.ai', logo: '/keyai.png', dark: true },
    { name: 'manest.ai', logo: '/manest1.png' },
    // Anonymised — no logo, always a name badge.
    { name: 'Dubai client' },
    { name: 'US VC' },
  ];

  return (
    <section className="relative py-10 lg:py-12 bg-background border-y border-border/50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Selected by hiring leaders at
          </p>
        </div>
      </div>

      {/* ─── Marquee rail (full-bleed, edge-faded) ─── */}
      <div
        className="relative group/logos"
        style={{
          maskImage:
            'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
        }}
      >
        <div className="flex w-max logo-track">
          {/* Two copies — the -50% translate lands on the start of the
              second copy, so the loop reads as a seamless scroll. */}
          {[0, 1].map((copy) => (
            <div
              key={copy}
              aria-hidden={copy === 1}
              className="flex items-center gap-10 sm:gap-14 pr-10 sm:pr-14"
            >
              {customers.map((c) => (
                <LogoMark
                  key={`${copy}-${c.name}`}
                  name={c.name}
                  logo={c.logo}
                  dark={c.dark}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="mt-8 text-center text-xs text-muted-foreground/70">
          From series-A startups to multi-thousand-employee enterprises —
          across India, the Gulf and the US
        </p>
      </div>

      <style>{`
        @keyframes logo-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .logo-track { animation: logo-scroll 26s linear infinite; }
        .group\\/logos:hover .logo-track { animation-play-state: paused; }

        /* Reduced motion: stop the rail and let it sit as a row. */
        @media (prefers-reduced-motion: reduce) {
          .logo-track { animation: none; }
        }
      `}</style>
    </section>
  );
}

/**
 * LogoMark — renders the real logo image when available (grayscale →
 * full colour on hover, the classic logo-wall treatment). If no logo
 * is set, or the file fails to load, it falls back to a monogram tile +
 * wordmark so the entry always shows something clean.
 */
function LogoMark({ name, logo, dark }) {
  const [imgError, setImgError] = useState(false);

  if (logo && !imgError) {
    const img = (
      <img
        src={logo}
        alt={name}
        title={name}
        onError={() => setImgError(true)}
        draggable="false"
        className="h-9 sm:h-12 w-auto max-w-[180px] object-contain opacity-95 hover:opacity-100 hover:scale-105 transition-all duration-300 select-none"
      />
    );
    // A light/white logo (e.g. key.ai) is invisible on the cream page —
    // give just that one a dark rounded backdrop so it stays readable.
    if (dark) {
      return (
        <span className="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-4 py-2.5 shadow-sm">
          {img}
        </span>
      );
    }
    return img;
  }

  const initials = name
    .split(/[\s-]+/)
    .map((w) => w.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <span className="flex items-center gap-2.5 opacity-50 hover:opacity-100 grayscale hover:grayscale-0 transition-all duration-300 cursor-default select-none whitespace-nowrap">
      <span className="w-8 h-8 rounded-lg bg-foreground/10 border border-border flex items-center justify-center text-[11px] font-black text-foreground/70 tracking-tight">
        {initials}
      </span>
      <span className="text-base sm:text-lg font-bold tracking-tight text-foreground/80">
        {name}
      </span>
    </span>
  );
}
